/* eslint-disable no-console */
/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import {
  Heading,
  HeadingLevel,
  Button,
  BrandButton,
  Link,
  Colors,
  Typography,
  Icon,
  TagLabel,
  Grid,
} from '../../../index';
import { IconName } from '../../display/Icon';

export type Props = {
  /** Background color of the entire element */
  backgroundColor?: 'blue10' | 'cream' | 'neutral100' | 'neutral200';

  /** Color style of the primary CTA button */
  ctaColor?: 'primary' | 'primary-alt' | 'secondary' | 'brand';

  /** href for the primary CTA button */
  ctaHref?: string;

  /** Text content to render for the primary CTA button */
  ctaText?: React.ReactNode;

  /** Text content to render for the description */
  description?: React.ReactNode;

  /** Text content to render for the heading */
  heading: string;

  /** Reverses the order of the text content and media */
  isReversed?: boolean;

  /** Media to render */
  media?: React.ReactNode;

  /** Click handler for the primary CTA button */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;

  /** Key down handler for the primary CTA button */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;

  /** Click handler for the secondary CTA button */
  onSecondaryCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;

  /** Key down handler for the secondary CTA button */
  onSecondaryCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;

  /** Text content to render for the secondary CTA button */
  secondaryCtaText?: React.ReactNode;

  /** href for the secondary CTA button */
  secondaryCtaHref?: string;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   */
  labelIcon?: IconName;
  /**
   * The labelColor determines the background-color of the label.
   */
  labelColor?:
    | 'info'
    | 'success'
    | 'error'
    | 'warning'
    | 'white'
    | 'gray'
    | 'peach'
    | 'neutral300'
    | 'bolt'
    | 'cream';
};

const Styles = labelStyles('TwoColumnRiver', {
  root: (backgroundColor: Props['backgroundColor'] = 'neutral100') => css`
    background-color: ${Colors[backgroundColor]};
    padding: 20px;

    @media screen and (min-width: 640px) {
      padding: 40px 30px;
    }

    @media screen and (min-width: 1024px) {
      padding: 60px 20px;
    }
  `,
  container: (isReversed: boolean) => css`
    max-width: 1170px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column-reverse;

    @media screen and (min-width: 1024px) {
      flex-direction: ${isReversed ? 'row-reverse' : 'row'};
    }
  `,
  content: css`
    width: 100%;

    @media screen and (min-width: 1024px) {
      max-width: 500px;
      padding-right: 30px;
    }
  `,
  labelContainer: css`
    margin-bottom: 20px;
  `,
  media: (isReversed: boolean) => css`
    margin-bottom: 20px;
    width: 100%;

    @media screen and (min-width: 1024px) {
      max-width: 500px;
      margin: ${isReversed ? '0 40px 0 0' : '0 0 0 40px'};
    }
  `,
  heading: css`
    ${Typography.pageHeader9}
    color: ${Colors.neutral800};
    margin-bottom: 20px;
    @media screen and (min-width: 640px) {
      ${Typography.pageHeader7}
    }

    @media screen and (min-width: 1024px) {
      ${Typography.pageHeader5}
    }
  `,
  description: css`
    ${Typography.paragraph1Short}
    color: ${Colors.neutral800};
    margin-bottom: 20px;
  `,
  cta: css`
    & > a,
    & > button {
      width: 100%;
    }

    @media screen and (min-width: 640px) {
      & > a,
      & > button {
        width: auto;
      }
    }
  `,
  secondaryCta: css`
    margin-top: 20px;
  `,
});

export const TwoColumnRiver = ({
  backgroundColor = 'neutral100',
  ctaColor = 'primary',
  ctaHref,
  ctaText,
  description,
  heading,
  isReversed = false,
  media,
  onCtaClick,
  onCtaKeyDown,
  onSecondaryCtaClick,
  onSecondaryCtaKeyDown,
  secondaryCtaHref,
  secondaryCtaText,
  label,
  labelIcon,
  labelColor,
}: Props) => {
  const getCtaComponent = () => {
    const sharedProps = {
      children: ctaText,
      href: ctaHref,
      onClick: onCtaClick,
      onKeyDown: onCtaKeyDown,
    };

    return ctaColor === 'brand' ? (
      <BrandButton {...sharedProps} />
    ) : (
      <Button {...sharedProps} color={ctaColor} />
    );
  };
  return (
    <div css={Styles.root(backgroundColor)}>
      <div css={Styles.container(isReversed)}>
        <div css={Styles.content}>
          {label && (
            <div css={Styles.labelContainer}>
              <TagLabel color={labelColor}>
                {labelIcon ? (
                  <Grid alignItems="center" columns="auto max-content" gap={5}>
                    <Icon name={labelIcon} size={20} />
                    {label}
                  </Grid>
                ) : (
                  label
                )}
              </TagLabel>
            </div>
          )}
          <HeadingLevel>
            <Heading css={Styles.heading}>{heading}</Heading>
          </HeadingLevel>
          {description && <div css={Styles.description}>{description}</div>}
          {ctaText && (ctaHref || onCtaClick) && (
            <div css={Styles.cta}>{getCtaComponent()}</div>
          )}
          {secondaryCtaText && secondaryCtaHref && (
            <div css={Styles.secondaryCta}>
              <Link
                href={secondaryCtaHref}
                onClick={onSecondaryCtaClick}
                onKeyDown={onSecondaryCtaKeyDown}
                weight="bold"
              >
                {secondaryCtaText}
              </Link>
            </div>
          )}
        </div>
        {media && <div css={Styles.media(isReversed)}>{media}</div>}
      </div>
    </div>
  );
};
